import React, { useState, useEffect, useMemo, useCallback } from 'react';
import supabase from '../../supabase';
import QuotationPDF from './QuotationPDF';
import { PDFViewer, pdf } from '@react-pdf/renderer';
import './QuotationForm.css';
import ClientSelection from '../Invoice/ClientSelection';
import AddClientPopup from '../Invoice/AddClientPopup';
import QuotationDetails from './QuotationDetails';
import QuotationOptions from './QuotationOptions';
import TermsConditions from '../Invoice/TermsConditions';
import QuoteItemsSection from './QuoteItemsSection';
import QuotationSummary from '../Invoice/InvoiceSummary';
import { useToast } from '../Invoice/Toast';

const QuotationForm = React.memo(() => {
  const [user, setUser] = useState(null);
  const [fromInfo, setFromInfo] = useState(null);
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [newClient, setNewClient] = useState({ name: '', email: '', company: '', gst: '', address: '' });
  const [showAddClientPopup, setShowAddClientPopup] = useState(false);
  const [quotationInfo, setQuotationInfo] = useState({ 
    number: '', 
    issueDate: new Date().toISOString().split('T')[0] 
  });
  const [items, setItems] = useState([{ 
    product_id: '', 
    product_name: '', 
    unit_price: 0, 
    quantity: 1, 
    hsn_code: '', 
    description: '' 
  }]);
  const [availableItems, setAvailableItems] = useState([]);
  const [banks, setBanks] = useState([]);
  const [selectedBankId, setSelectedBankId] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [termsAndConditions, setTermsAndConditions] = useState(['']);
  const [showHsnColumn, setShowHsnColumn] = useState(true);
  const [taxType, setTaxType] = useState('NONE');
  const [gstRate, setGstRate] = useState(18);
  const [igstRate, setIgstRate] = useState(18);
  const [discount, setDiscount] = useState(0);
  const [hasdiscount, setHasdiscount] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [pdfKey, setPdfKey] = useState(0);
  const [sendingEmail, setSendingEmail] = useState(false);
  const { showError, showSuccess, showWarning, showInfo, ToastContainer } = useToast();
  
  // Memoized calculations
  const sgstRate = useMemo(() => gstRate / 2, [gstRate]);
  const cgstRate = useMemo(() => gstRate / 2, [gstRate]);
  
  // Memoized mobile check function
  const checkMobile = useCallback(() => {
    setIsMobile(window.innerWidth < 801);
  }, []);

  useEffect(() => {
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [checkMobile]);

  useEffect(() => {
    if (showPreview) {
      const timer = setTimeout(() => {
        setPdfKey(prev => prev + 1);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [items, showPreview]);

  useEffect(() => {
    const fetchUserAndData = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        setUser(user);
        if (!user) return;

        const [
          { data: userProfile },
          { data: clientList },
          { data: productTemplates },
          { data: bankList }
        ] = await Promise.all([
          supabase.from('users').select('*').eq('id', user.id).single(),
          supabase.from('clients').select('*').eq('user_id', user.id),
          supabase.from('products').select('*').eq('user_id', user.id),
          supabase.from('banks').select('*').eq('user_id', user.id)
        ]);

        setFromInfo(userProfile || {});
        setClients(clientList || []);
        setAvailableItems(productTemplates || []);
        setBanks(bankList || []);
        
        if (bankList && bankList.length > 0) {
          setSelectedBankId(bankList[0].id);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        showError('Data Load Error', 'Failed to load quotation data');
      }
    };
    fetchUserAndData();
  }, [showError]);

  const selectedClient = selectedClientId ? clients.find(c => c.id === selectedClientId) : null;
  const selectedBank = selectedBankId ? banks.find(b => b.id === selectedBankId) : null;

  // Memoized callback functions
  const handleClientSelect = useCallback((clientId) => {
    setSelectedClientId(clientId);
    if (clientId) {
      const selectedClient = clients.find(c => c.id === clientId);
      if (selectedClient) {
        setNewClient({
          name: selectedClient.client_name || '',
          email: selectedClient.client_email || '',
          company: selectedClient.client_company_name || '',
          gst: selectedClient.client_gst_number || '',
          address: selectedClient.address || ''
        });
      }
    } else {
      setNewClient({ name: '', email: '', company: '', gst: '', address: '' });
    }
  }, [clients]);

  const handleAddClientFromPopup = useCallback(() => {
    setShowAddClientPopup(false);
    setSelectedClientId('');
  }, []);

  const handleCancelAddClient = useCallback(() => {
    setShowAddClientPopup(false);
    setNewClient({ name: '', email: '', company: '', gst: '', address: '' });
  }, []);

  const handleProductSelect = useCallback((index, productId) => {
    setItems(prevItems => {
      const updated = [...prevItems];
      if (productId) {
        const product = availableItems.find(p => p.product_id === parseInt(productId));
        if (product) {
          updated[index] = {
            ...updated[index],
            product_id: parseInt(productId), // Convert to number for consistency
            product_name: product.product_name,
            unit_price: product.price_selling,
            hsn_code: product.hsn_code,
            description: product.description
          };
        }
      } else {
        updated[index] = {
          ...updated[index],
          product_id: '',
          product_name: '',
          unit_price: 0,
          hsn_code: '',
          description: ''
        };
      }
      return updated;
    });
  }, [availableItems]);

  const handleItemChange = useCallback((index, field, value) => {
    setItems(prevItems => {
      const updated = [...prevItems];
      updated[index][field] = value;
      return updated;
    });
  }, []);

  const addItem = useCallback(() => {
    setItems(prevItems => [...prevItems, { 
      product_id: '', 
      product_name: '', 
      unit_price: 0, 
      quantity: 1, 
      hsn_code: '', 
      description: '' 
    }]);
  }, []);

  const removeItem = useCallback((index) => {
    setItems(prevItems => prevItems.filter((_, i) => i !== index));
  }, []);

  const getItemTotal = useCallback((item) => {
    const qty = typeof item.quantity === 'string' ? parseInt(item.quantity) || 0 : item.quantity;
    return qty * item.unit_price;
  }, []);

  const calculateItemTax = useCallback((item) => {
    const qty = typeof item.quantity === 'string' ? parseInt(item.quantity) || 0 : item.quantity;
    const base = qty * item.unit_price;
    
    if (taxType === 'SGST_CGST') {
      return base * ((sgstRate + cgstRate) / 100);
    } else if (taxType === 'IGST') {
      return base * (igstRate / 100);
    }
    return 0;
  }, [taxType, sgstRate, cgstRate, igstRate]);

  const addTerm = useCallback(() => {
    setTermsAndConditions(prev => [...prev, '']);
  }, []);

  const removeTerm = useCallback((index) => {
    setTermsAndConditions(prev => 
      prev.length > 1 ? prev.filter((_, i) => i !== index) : prev
    );
  }, []);

  const handleTermChange = useCallback((index, value) => {
    setTermsAndConditions(prev => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
  }, []);

  // Helper functions for calculations
  const numberToWords = useCallback((num) => {
    if (!num || num === 0) return 'Zero only';
    return `${Math.round(num)} only`;
  }, []);

  const getLocalItemTotal = useCallback((item) => {
    const qty = typeof item.quantity === 'string' ? parseInt(item.quantity) || 0 : item.quantity;
    return qty * (item.unit_price || 0);
  }, []);

  const calculateLocalItemTax = useCallback((item) => {
    return calculateItemTax(item);
  }, [calculateItemTax]);

  // Memoized summary calculation
  const calculateSummary = useMemo(() => {
    let subtotal = 0, totalTax = 0;
    let sgstTotal = 0, cgstTotal = 0, igstTotal = 0;
    
    items.forEach(item => {
      if (!item.product_name || !item.unit_price) return;
      const qty = typeof item.quantity === 'string' ? parseInt(item.quantity) || 0 : item.quantity;
      const base = qty * item.unit_price;
      subtotal += base;
      
      if (taxType === 'SGST_CGST') {
        const sgst = base * (sgstRate / 100);
        const cgst = base * (cgstRate / 100);
        sgstTotal += sgst;
        cgstTotal += cgst;
        totalTax += sgst + cgst;
      } else if (taxType === 'IGST') {
        const igst = base * (igstRate / 100);
        igstTotal += igst;
        totalTax += igst;
      }
    });
    
    let Discount = 0;
    let total = subtotal + totalTax;
    
    if (hasdiscount) {
      Discount = subtotal * (discount / 100);
      const subtotal_d = subtotal - Discount;
      if (taxType === 'SGST_CGST') {
        sgstTotal = subtotal_d * (sgstRate / 100);
        cgstTotal = subtotal_d * (cgstRate / 100);
        totalTax = sgstTotal + cgstTotal;
      } else if (taxType === 'IGST') {
        igstTotal = subtotal_d * (igstRate / 100);
        totalTax = igstTotal;
      }
      total = subtotal_d + totalTax;
    }

    return { subtotal, totalTax, total, sgstTotal, cgstTotal, igstTotal, Discount, discountAmount: Discount };
  }, [items, taxType, sgstRate, cgstRate, igstRate, hasdiscount, discount]);

  // Memoized safe items
  const memoizedSafeItems = useMemo(() => {
    const validItems = items.filter(item => item.product_name && item.unit_price);
    return validItems.length > 0 ? validItems.map((item, index) => ({
      ...item,
      stableKey: `item-${item.product_id || index}-${item.product_name || 'empty'}`
    })) : [{
      product_id: '1',
      product_name: 'Sample Product',
      unit_price: 100,
      quantity: 1,
      hsn_code: '1234',
      description: 'Sample description',
      stableKey: 'sample-item-1'
    }];
  }, [items]);

  // Memoized quotation data
  const memoizedQuotationData = useMemo(() => {
    const safeFromInfo = fromInfo || {
      company_name: 'Your Company',
      name: 'Your Name',
      address: '',
      phone: '',
      email: '',
      gst_number: ''
    };
    const safeNewClient = newClient || {
      name: 'Customer Name',
      email: '',
      company: '',
      gst: '',
      address: ''
    };
    const safeQuotationInfo = quotationInfo || {
      number: 'QUO-001',
      issueDate: new Date().toISOString().split('T')[0]
    };
    const safeTermsAndConditions = termsAndConditions.length > 0 ? termsAndConditions : ['Quote valid for 30 days'];
    return {
      fromInfo: safeFromInfo,
      selectedClient,
      newClient: safeNewClient,
      quotationInfo: safeQuotationInfo,
      termsAndConditions: safeTermsAndConditions,
      userProfile: safeFromInfo
    };
  }, [fromInfo, selectedClient, newClient, quotationInfo, termsAndConditions]);
  
  // Comprehensive validation function
  const validateQuotation = useCallback(() => {
    const errors = [];

    // Check user authentication
    if (!user) {
      showError('Authentication Error', 'Please log in to create a quotation');
      return false;
    }

    // Validate quotation number
    if (!quotationInfo.number || quotationInfo.number.trim() === '') {
      errors.push('Quotation number is required');
    }

    // Validate quotation date
    if (!quotationInfo.issueDate) {
      errors.push('Issue date is required');
    }

    // Validate client information
    if (!selectedClientId && !newClient.name) {
      errors.push('Please select a client or add client name');
    }

    if (newClient.name && !newClient.email) {
      errors.push('Client email is required');
    }

    // Validate email format if provided
    if (newClient.email && newClient.email.trim() !== '') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(newClient.email)) {
        errors.push('Please enter a valid client email address');
      }
    }

    // Validate items
    const validItems = items.filter(item => item.product_name && item.unit_price > 0);
    if (validItems.length === 0) {
      errors.push('Please add at least one item with name and price');
    }

    // Check each item for completeness
    items.forEach((item, index) => {
      if (item.product_name && !item.unit_price) {
        errors.push(`Item ${index + 1}: Please enter a price`);
      }
      if (item.product_name && (!item.quantity || item.quantity <= 0)) {
        errors.push(`Item ${index + 1}: Please enter a valid quantity`);
      }
    });

    // Show all validation errors
    if (errors.length > 0) {
      errors.forEach((error, index) => {
        setTimeout(() => {
          showError('Validation Error', error);
        }, index * 150); // Stagger the error messages
      });
      return false;
    }

    return true;
  }, [user, quotationInfo, selectedClientId, newClient, items, showError]);

  const handleSave = useCallback(async () => {
    // Validate before saving
    if (!validateQuotation()) {
      return null;
    }

    try {
      let clientId = selectedClientId;
      if (!clientId && newClient.name) {
        const { data: insertedClient, error } = await supabase.from('clients').insert([{
          user_id: user.id,
          client_name: newClient.name,
          client_email: newClient.email,
          client_company_name: newClient.company,
          client_gst_number: newClient.gst,
          address: newClient.address
        }]).select().single();
        
        if (error) {
          showError('Client Error', `Failed to add client: ${error.message}`);
          return null;
        }
        clientId = insertedClient.id;
      }

      const quotationItems = items.filter(item => item.product_name && item.unit_price > 0);

      const { subtotal, totalTax, total, Discount } = calculateSummary;
      const gstRate_final = taxType === 'SGST_CGST' ? (sgstRate + cgstRate) : 0;
      const igstRate_final = taxType === 'IGST' ? igstRate : 0;

      const { data: quotationData, error: quotationError } = await supabase
        .from('quotes')
        .insert([{
          user_id: user.id,
          quotation_number: quotationInfo.number,
          client_id: clientId,
          total_amount: total,
          discount: hasdiscount ? discount : 0,
          gst: gstRate_final,
          i_gst: igstRate_final,
          terms_conditions: termsAndConditions.filter(term => term.trim() !== '').join('|')
        }])
        .select()
        .single();

      if (quotationError) {
        showError('Quotation Error', `Failed to create quotation: ${quotationError.message}`);
        return null;
      }

      const quoteItemsData = quotationItems.map(item => {
        const qty = typeof item.quantity === 'string' ? parseInt(item.quantity) || 0 : item.quantity;
        const unitPrice = parseFloat(item.unit_price) || 0;
        const totalValue = qty * unitPrice;

        return {
          quote_id: quotationData.id,
          user_id: user.id,
          p_name: item.product_name,
          description: item.description || '',
          quantity: qty,
          unit_price: unitPrice,
          total_value: totalValue
        };
      });

      const { error: itemsError } = await supabase
        .from('quote_items')
        .insert(quoteItemsData);

      if (itemsError) {
        await supabase.from('quotes').delete().eq('id', quotationData.id);
        showError('Items Error', `Failed to add quotation items: ${itemsError.message}`);
        return null;
      }

      return quotationData.id;
    } catch (error) {
      console.error('Error saving quotation:', error);
      showError('Save Failed', error.message || 'An unexpected error occurred');
      return null;
    }
  }, [user, quotationInfo, selectedClientId, newClient, calculateSummary, termsAndConditions, items, taxType, sgstRate, cgstRate, igstRate, hasdiscount, discount, showError, validateQuotation]);

  const handleSend = useCallback(async () => {
    if (sendingEmail) return;
    
    try {
      setSendingEmail(true);
      const quotationId = await handleSave();
      if (!quotationId) {
        setSendingEmail(false);
        return;
      }

      let clientEmail = '';
      let clientName = '';
      
      if (selectedClientId) {
        const client = clients.find(c => c.id === selectedClientId);
        clientEmail = client?.client_email;
        clientName = client?.client_name;
      } else if (newClient.email) {
        clientEmail = newClient.email;
        clientName = newClient.name;
      }

      if (!clientEmail) {
        showError('Email Missing', 'Client email address is required to send quotation');
        setShowPreview(false);
        setSendingEmail(false);
        return;
      }

      const pdfDocument = (
        <QuotationPDF
          quotationData={memoizedQuotationData}
          items={memoizedSafeItems}
          localSummary={calculateSummary}
          numberToWords={numberToWords}
          getLocalItemTotal={getLocalItemTotal}
          calculateLocalItemTax={calculateLocalItemTax}
          showTaxPerItemColumn={taxType !== 'NONE'}
          showHsnColumn={showHsnColumn}
          hasTax={taxType !== 'NONE'}
          hasdiscount={hasdiscount}
          discount={discount}
          taxType={taxType}
          sgstRate={sgstRate}
          cgstRate={cgstRate}
          igstRate={igstRate}
          selectedBank={null}
          qrCodeDataURL=""
          showValueColumn={taxType !== 'NONE'}
        />
      );
      
      const pdfBlob = await pdf(pdfDocument).toBlob();
      const pdfArrayBuffer = await pdfBlob.arrayBuffer();
      const pdfBase64 = btoa(String.fromCharCode(...new Uint8Array(pdfArrayBuffer)));

      const res = await fetch(
        'https://dzwtnmlmjwbwqevbhwnv.supabase.co/functions/v1/send-invoice-email',
        {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${supabase.supabaseKey}`
          },
          body: JSON.stringify({
            from: 'no-reply@bossbyko.com',
            to: clientEmail,
            subject: `Quotation ${quotationInfo.number}`,
            html: `<p>Hello ${clientName || 'Valued Customer'},</p>
                  <p>Please find attached quotation <strong>${quotationInfo.number}</strong>.</p>
                  <p>Best regards,<br>${fromInfo?.company_name || fromInfo?.name || 'Your Company'}</p>`,
            attachments: [
              {
                filename: `Quotation_${quotationInfo.number}.pdf`,
                content: pdfBase64,
                encoding: 'base64',
                contentType: 'application/pdf'
              }
            ]
          })
        }
      );

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const result = await res.json();
      if (!result.success) {
        throw new Error(result.error || 'Failed to send email');
      }

      const { error: updateErr } = await supabase
        .from('quotes')
        .update({ email_sent: true })
        .eq('id', quotationId)
        .eq('user_id', user.id);

      if (updateErr) {
        console.error('Error updating email_sent status:', updateErr);
        showWarning('Sent with Warning', 'Quotation sent but status not updated');
      } else {
        showSuccess('Success!', `Quotation saved and sent successfully to ${clientEmail}`);
      }
      
      setShowPreview(false);
      
      setQuotationInfo({ 
        number: '', 
        issueDate: new Date().toISOString().split('T')[0] 
      });
      setItems([{ 
        product_id: '', 
        product_name: '', 
        unit_price: 0, 
        quantity: 1, 
        hsn_code: '', 
        description: '' 
      }]);
      setSelectedClientId('');
      setNewClient({ name: '', email: '', company: '', gst: '', address: '' });
      setTermsAndConditions(['']);
      
    } catch (error) {
      console.error('Error sending quotation:', error);
      showError('Send Failed', error.message || 'Could not send quotation');
    } finally {
      setSendingEmail(false);
    }
  }, [handleSave, selectedClientId, clients, newClient, memoizedQuotationData, memoizedSafeItems, calculateSummary, numberToWords, getLocalItemTotal, calculateLocalItemTax, taxType, showHsnColumn, hasdiscount, discount, sgstRate, cgstRate, igstRate, quotationInfo, fromInfo, user, sendingEmail, showError, showWarning, showSuccess]);

  const handleSaveOnly = useCallback(async () => {
    try {
      const quotationId = await handleSave();
      if (quotationId) {
        showSuccess('Success!', 'Quotation saved successfully');
        setShowPreview(false);
        
        setQuotationInfo({ 
          number: '', 
          issueDate: new Date().toISOString().split('T')[0]
        });
        setItems([{ 
          product_id: '', 
          product_name: '', 
          unit_price: 0, 
          quantity: 1, 
          hsn_code: '', 
          description: '' 
        }]);
        setSelectedClientId('');
        setNewClient({ name: '', email: '', company: '', gst: '', address: '' });
        setTermsAndConditions(['']);
      }
    } catch (error) {
      console.error('Error saving quotation:', error);
      showError('Save Failed', error.message || 'An error occurred while saving');
    }
  }, [handleSave, showSuccess, showError]);

  const handleBack = useCallback(() => {
    setShowPreview(false);
    setPdfError(null);
  }, []);

  return (
    <div className="QuotationForm__container">
      <h1 className="QuotationForm__title">Create Quotation</h1>

      <section className="QuotationForm__details-format-wrapper">
        <div className="QuotationForm__details-box">
          <div className="QuotationForm__box-content">
            <h2 className="QuotationForm__section-title">Client & Quotation Details</h2>
            <div className="QuotationForm__horizontal-sections">
              <ClientSelection
                clients={clients}
                selectedClientId={selectedClientId}
                newClient={newClient}
                onClientSelect={handleClientSelect}
                onShowAddClient={() => setShowAddClientPopup(true)}
              />
              <QuotationDetails
                quotationInfo={quotationInfo}
                setQuotationInfo={setQuotationInfo}
              />
            </div>
          </div>
        </div>
        
        <div className="QuotationForm__format-box">
          <div className="QuotationForm__box-content">
            <h2 className="QuotationForm__section-title">Quotation Format</h2>
            <QuotationOptions
              showHsnColumn={showHsnColumn}
              setShowHsnColumn={setShowHsnColumn}
              taxType={taxType}
              setTaxType={setTaxType}
              gstRate={gstRate}
              setGstRate={setGstRate}
              igstRate={igstRate}
              setIgstRate={setIgstRate}
              hasdiscount={hasdiscount}
              setHasdiscount={setHasdiscount}
              discount={discount}
              setDiscount={setDiscount}
              isMobile={isMobile}
            />
          </div>
        </div>
      </section>
      
      {showAddClientPopup && (
        <AddClientPopup
          newClient={newClient}
          setNewClient={setNewClient}
          onSave={handleAddClientFromPopup}
          onCancel={handleCancelAddClient}
        />
      )}
      
      <QuoteItemsSection
        items={items}
        availableItems={availableItems}
        showHsnColumn={showHsnColumn}
        taxType={taxType}
        hasdiscount={hasdiscount}
        isMobile={isMobile}
        onProductSelect={handleProductSelect}
        onItemChange={handleItemChange}
        onAddItem={addItem}
        onRemoveItem={removeItem}
        getItemTotal={getItemTotal}
        calculateItemTax={calculateItemTax}
      />

      <section className="InvoiceForm__terms-summary-container">
          <div className="InvoiceForm__terms-summary-wrapper">
            <div className="InvoiceForm__terms-box">
              <div className="InvoiceForm__box-content">
              <TermsConditions
                termsAndConditions={termsAndConditions}
                onTermChange={handleTermChange}
                onAddTerm={addTerm}
                onRemoveTerm={removeTerm}
                isMobile={isMobile}
              />
            </div>
          </div>

          <div className="InvoiceForm__summary-box">
              <div className="InvoiceForm__box-content">
              <QuotationSummary
                calculateSummary={calculateSummary}
                taxType={taxType}
                sgstRate={sgstRate}
                cgstRate={cgstRate}
                igstRate={igstRate}
                hasdiscount={hasdiscount}
                discount={discount}
              />
            </div>
          </div>
        </div>
      </section>
      
      <button 
        onClick={() => {
          setPdfError(null);
          setShowPreview(true);
        }} 
        className="QuotationForm__button QuotationForm__button--preview"
      >
        Preview Quotation
      </button>
      
      {showPreview && (
        <div className="QuotationForm__pdf-popup-overlay" key={`pdf-overlay-${pdfKey}`}>
          <div className="QuotationForm__pdf-popup">
            {pdfError ? (
              <div className="QuotationForm__pdf-error">
                <h3>PDF Error:</h3>
                <p>{pdfError}</p>
                <button onClick={() => {
                  setPdfError(null);
                  setPdfKey(prev => prev + 1);
                }}>Retry</button>
              </div>
            ) : (
              <PDFViewer 
                width="100%" 
                height="100%" 
                className="QuotationForm__pdf-viewer"
                key={`pdf-viewer-${pdfKey}`}
                onError={(error) => {
                  console.error('PDF Viewer Error:', error);
                  setPdfError(error.message || 'Failed to render PDF');
                  showError('PDF Error', error.message || 'Failed to render PDF');
                }}
              >
                <QuotationPDF
                  quotationData={memoizedQuotationData}
                  items={memoizedSafeItems}
                  localSummary={calculateSummary}
                  numberToWords={numberToWords}
                  getLocalItemTotal={getLocalItemTotal}
                  calculateLocalItemTax={calculateLocalItemTax}
                  showTaxPerItemColumn={taxType !== 'NONE'}
                  showHsnColumn={showHsnColumn}
                  hasTax={taxType !== 'NONE'}
                  hasdiscount={hasdiscount}
                  discount={discount}
                  taxType={taxType}
                  sgstRate={sgstRate}
                  cgstRate={cgstRate}
                  igstRate={igstRate}
                  selectedBank={null}
                  qrCodeDataURL=""
                  showValueColumn={taxType !== 'NONE'}
                />
              </PDFViewer>
            )}
            <div className="QuotationForm__pdf-popup-actions">
              <button 
                onClick={handleBack} 
                className="QuotationForm__button QuotationForm__button--popup"
                disabled={sendingEmail}
              >
                Back & Edit
              </button>
              <button 
                onClick={handleSaveOnly} 
                className="QuotationForm__button QuotationForm__button--popup"
                disabled={sendingEmail}
              >
                Save
              </button>
              <button 
                onClick={handleSend} 
                className="QuotationForm__button QuotationForm__button--popup"
                disabled={sendingEmail}
              >
                {sendingEmail ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Toast Notifications Container */}
      <ToastContainer />
    </div>
  );
});

QuotationForm.displayName = 'QuotationForm';
export default QuotationForm;